import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Wallet, ArrowDownCircle, ArrowUpCircle, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const DENOMS = [2000, 500, 200, 100, 50, 20, 10, 5, 2, 1];

export default function Shifts() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);

  const [openDlg, setOpenDlg] = useState(false);
  const [openingCash, setOpeningCash] = useState(0);

  const [closeDlg, setCloseDlg] = useState(false);
  const [denoms, setDenoms] = useState({});
  const closingCash = DENOMS.reduce((a, d) => a + d * Number(denoms[d] || 0), 0);

  const reload = async () => {
    const [c, h] = await Promise.all([
      api.get("/shifts/current"),
      api.get("/shifts", { params: { limit: 30 } }),
    ]);
    setCurrent(c.data || null);
    setHistory(h.data || []);
  };
  useEffect(() => { reload(); }, []);

  const openShift = async () => {
    try {
      await api.post("/shifts/open", { opening_cash: Number(openingCash || 0) });
      toast.success("Shift started");
      setOpenDlg(false);
      setOpeningCash(0);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeShift = async () => {
    try {
      const { data } = await api.post(`/shifts/${current.id}/close`, {
        closing_cash: closingCash,
        denominations: denoms,
      });
      toast.success(`Shift closed · Variance ${inr(data.variance)}`);
      setCloseDlg(false);
      setDenoms({});
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="shifts-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Shift &amp; Cash Drawer</h1>
        <p className="text-sm text-[#7A7873] mt-1">Open shift with cash float, close with denomination count, see Z-report.</p>
      </div>

      {!current ? (
        <div className="bg-white border border-[#EBE8E2] rounded-lg p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-[#FBE9E2] text-[#D95D39] flex items-center justify-center mb-3">
            <Wallet size={22} />
          </div>
          <div className="font-heading font-bold text-lg text-[#2D2C2A]">No open shift</div>
          <div className="text-sm text-[#7A7873] mb-4">Start a shift to track cash drawer activity for the day.</div>
          <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={() => setOpenDlg(true)} data-testid="open-shift-btn">
            <ArrowDownCircle size={16} className="mr-1.5" /> Open Shift
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-[#EBE8E2] rounded-lg p-6">
          <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6A7F66]">Open Shift</div>
              <div className="font-heading font-bold text-2xl text-[#2D2C2A]">{current.user_name}</div>
              <div className="text-xs text-[#7A7873]">Started {new Date(current.opened_at).toLocaleString("en-IN")}</div>
            </div>
            <Button className="bg-[#2D2C2A] hover:bg-black" onClick={() => setCloseDlg(true)} data-testid="close-shift-btn">
              <ArrowUpCircle size={16} className="mr-1.5" /> Close Shift
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <Card label="Opening Cash" value={inr(current.opening_cash)} />
            <Card label="Active Since" value={`${Math.round((Date.now() - new Date(current.opened_at).getTime()) / 3600000)}h`} />
            <Card label="Cashier" value={current.user_name} />
            <Card label="Status" value={<span className="text-[#6A7F66]">OPEN</span>} />
          </div>
        </div>
      )}

      <h2 className="font-heading font-bold text-xl text-[#2D2C2A] mt-8 mb-3 flex items-center gap-2">
        <ClipboardList size={18} /> Z-Reports (last 30)
      </h2>
      <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Cashier</th>
              <th className="text-right px-4 py-2.5">Orders</th>
              <th className="text-right px-4 py-2.5">Sales</th>
              <th className="text-right px-4 py-2.5">Cash</th>
              <th className="text-right px-4 py-2.5">UPI</th>
              <th className="text-right px-4 py-2.5">Card</th>
              <th className="text-right px-4 py-2.5">Variance</th>
            </tr>
          </thead>
          <tbody>
            {history.map((s) => (
              <tr key={s.id} className="border-t border-[#EBE8E2]" data-testid={`shift-${s.id}`}>
                <td className="px-4 py-2.5 text-xs">{new Date(s.opened_at).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2.5">{s.user_name}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{s.order_count || 0}</td>
                <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(s.sales_total)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{inr(s.by_mode?.cash)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{inr(s.by_mode?.upi)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{inr(s.by_mode?.card)}</td>
                <td className={`px-4 py-2.5 text-right font-mono-num ${(s.variance || 0) < 0 ? "text-[#E76F51]" : "text-[#6A7F66]"}`}>
                  {s.status === "closed" ? inr(s.variance) : "—"}
                </td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#7A7873]">No closed shifts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={openDlg} onOpenChange={setOpenDlg}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle>Open Shift</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Opening Cash Float (₹)</Label>
            <Input type="number" value={openingCash} onChange={(e) => setOpeningCash(e.target.value)} data-testid="opening-cash-input" />
            <p className="text-xs text-[#7A7873]">The cash you put in the drawer to give change.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDlg(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={openShift} data-testid="confirm-open-shift">Open</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={closeDlg} onOpenChange={setCloseDlg}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Close Shift · Cash Count</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            <p className="text-xs text-[#7A7873]">Count each note/coin denomination — total auto-computes.</p>
            {DENOMS.map((d) => (
              <div key={d} className="grid grid-cols-[80px_1fr_120px] gap-2 items-center">
                <div className="font-mono-num font-semibold text-[#2D2C2A]">₹ {d}</div>
                <Input
                  type="number"
                  min={0}
                  placeholder="qty"
                  value={denoms[d] || ""}
                  onChange={(e) => setDenoms({ ...denoms, [d]: e.target.value })}
                  data-testid={`denom-${d}`}
                />
                <div className="text-right font-mono-num text-sm text-[#7A7873]">{inr(d * Number(denoms[d] || 0))}</div>
              </div>
            ))}
            <div className="border-t border-[#EBE8E2] mt-3 pt-3 flex justify-between items-center">
              <span className="font-semibold">Total Cash Counted</span>
              <span className="font-mono-num font-bold text-xl text-[#D95D39]" data-testid="closing-cash-total">{inr(closingCash)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDlg(false)}>Cancel</Button>
            <Button className="bg-[#2D2C2A] hover:bg-black" onClick={closeShift} data-testid="confirm-close-shift">Close & Generate Z-Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Card = ({ label, value }) => (
  <div className="border border-[#EBE8E2] rounded-md p-3 bg-[#FBFAF8]">
    <div className="text-[10px] uppercase tracking-wider text-[#7A7873]">{label}</div>
    <div className="font-mono-num font-bold text-lg text-[#2D2C2A]">{value}</div>
  </div>
);
